import type { ReactElement } from 'react';
import { useState, useRef } from 'react';
import { css, cx } from '@eti/styles';
import debounce from 'lodash/debounce';
import { vars, Stack, Inline } from '@etg/wings';

type PopularRoutesCarouselProps = {
  children: ReactElement[];
  'data-testid': string;
};

const wrapperStyles = css`
  margin-inline: -10px;
  overflow: hidden;
  position: relative;
  width: calc(100% + 20px);
`;

const contentStyles = css`
  display: flex;
  gap: 16px;
  -ms-overflow-style: none;
  overflow-x: scroll;
  padding: 8px 10px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  transition: transform 0.3s ease-in-out;

  [dir='rtl'] & {
    flex-flow: row-reverse;
  }

  &::-webkit-scrollbar {
    display: none;
    width: 0;
  }
`;

const dotSize = 10;
const gapSize = 8;
const widthOfVisibleDots = dotSize * 5 + gapSize * 4 + 4;
const widthOfTotalDots = dotSize * 8 + gapSize * 7 + 8;

const dotStyles = css`
  background: #fff;
  border: 1px solid ${vars.colors.links.default};
  border-radius: 50%;
  box-sizing: border-box;
  display: inline-block;
  height: ${dotSize}px;
  transition: background-color 0.3s ease-in-out;
  width: ${dotSize}px;
`;

const activeDotStyles = css`
  background-color: ${vars.colors.links.default};
  border: 1px solid ${vars.colors.links.default};
  height: ${dotSize}px;
  transform: scale(1.2);
  width: ${dotSize}px;
`;

const smallDotStyles = css`
  transform: scale(0.8);
`;

const dotsContentWrapperStyles = css`
  display: flex;
  height: 18px;
  overflow: hidden;
  padding: 3px;
  width: ${widthOfVisibleDots}px;

  [dir='rtl'] & {
    flex-flow: row-reverse;
  }
`;

const dotsContentStyles = (translateX: string) => css`
  display: flex;
  gap: ${gapSize}px;
  justify-content: center;
  transform: translateX(${translateX});
  transition: transform 0.3s ease-in-out;
  width: ${widthOfTotalDots}px;

  [dir='rtl'] & {
    flex-flow: row-reverse;
  }
`;

const itemStyles = css`
  flex: 0 0 80%;
  min-height: 1px;
  scroll-snap-align: center;
`;

const shouldBeSmall = (index: number, currDot: number) =>
  (currDot < 3 && index === 4) ||
  (currDot === 3 && [1, 5].includes(index)) ||
  (currDot === 4 && [2, 6].includes(index)) ||
  (currDot > 4 && index === 3);

const PopularRoutesCarousel = ({
  children,
  'data-testid': dataTestId,
}: PopularRoutesCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const slideRefs = useRef<HTMLDivElement[]>([]);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);

  const handleScroll = debounce((e) => {
    const widthOfOneElement = e.target.scrollWidth / children.length;
    const currIndex = e.target.scrollLeft / widthOfOneElement;
    const CurrentSlideIndex = Math.round(currIndex);
    setCurrentIndex(CurrentSlideIndex);
  }, 15);

  const numSlides = children ? children.length : 0;

  const calculateTransform = () => {
    if (currentIndex < 3) {
      return `0`;
    }
    if (currentIndex > numSlides - 3) {
      return `-${(numSlides - 5) * (dotSize + gapSize)}px`;
    }
    return `-${(currentIndex - 2) * (dotSize + gapSize)}px`;
  };

  return (
    <Stack className={wrapperStyles} data-testid={dataTestId} spacing={8}>
      <div
        className={contentStyles}
        data-testid="carousel-wrapper"
        onScroll={handleScroll}
        ref={carouselWrapperRef}
      >
        {children?.map((child, index: number) => (
          <div
            className={itemStyles}
            data-testid={`slide-${index}`}
            key={`${child?.key}-${index}`}
            ref={(ref) => {
              if (ref) {
                slideRefs.current[index] = ref;
              }
            }}
          >
            {child}
          </div>
        ))}
      </div>
      <Inline align="center">
        <div className={dotsContentWrapperStyles}>
          <ul className={dotsContentStyles(calculateTransform())}>
            {children?.map((child, index) => (
              <li
                aria-label={`show ${child?.props?.header}`}
                className={cx(
                  dotStyles,
                  currentIndex === index && cx('activeDotStyle', activeDotStyles),
                  shouldBeSmall(index, currentIndex) && cx('smallDotStyle', smallDotStyles),
                )}
                data-testid={`dot${index}`}
                key={`${child.key}-${index}-dot`}
              />
            ))}
          </ul>
        </div>
      </Inline>
    </Stack>
  );
};

export default PopularRoutesCarousel;
